import {
  Box,
  Flex,
  IconButton,
  Skeleton,
  Spinner,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import React, { Dispatch, SetStateAction, useState } from 'react';

import { CreditAgreement, Reporting } from '@finley-technologies/finley-types';
import { validate as reportingValidate } from '@finley-technologies/reporting-utilities';

import { useGetUserQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { ReportingGlobalRecurringDateConfigs } from '../../property/globalRecurringDate/ReportingGlobalRecurringDateConfigs';
import { ReportingDataAccessors } from './dataAccessors/ReportingDataAccessors';
import { ReportingIntermediateFields } from './intermediateField/ReportingIntermediateFields';
import { ReportingGenerateWorkleaseModal } from './ReportingGeneratingWorkleaseModal';
import { VariablePreviewModal } from './VariablePreviewModal';
import { AthenaQueriesView } from 'packages/athena/ui/AthenaQueryView';
import { AthenaRecurringQueriesContainer } from 'packages/athena/ui/AthenaRecurringQueryView';
import {
  SaveIconButton,
  UploadIconButton,
} from 'packages/common-frontend/components/IconButton';
import { CreditAgreementFullArtifactWipConfigType } from 'packages/creditAgreements/ui/types';
import { useGetEvaluatedReportInputConfigResultByCreditAgreementIdLazyQueryGQLType } from 'packages/reporting/requests/reporting.gql.generated';
import { FiSearch } from 'react-icons/fi';
import { getErrorMessageFromApolloError } from '~/contexts/ApolloClientProvider';
import { errorToastAdminConfig } from '~/packages/common-frontend/constants';

export const createReportingInputShell = (): CreditAgreement.Config.Reporting.Inputs.Config => {
  return {
    globalRecurringDateConfigs: [],
    athenaQueries: [],
    athenaRecurringQueries: [],
    dataAccessors: [],
    intermediateFields: [],
  };
};

type ReportingInputConfigProps = {
  organizationId: string;
  reportingProperty: CreditAgreement.DomainEntity.ReportingProperty;
  inputConfigPropertyKey: string;
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  queryResults: CreditAgreement.Config.QueryResult[];
  setQueryResults: Dispatch<
    SetStateAction<CreditAgreement.Config.QueryResult[]>
  >;
};
export const ReportingInputConfig = ({
  organizationId,
  inputConfigPropertyKey,
  reportingProperty,
  wipConfig,
  queryResults,
  setQueryResults,
}: ReportingInputConfigProps) => {
  const { _config_, name: reportName, reportConfigTypes } = reportingProperty;

  const toast = useToast();
  const [showVariableModal, setShowVariableModal] = useState<boolean>(false);
  const [
    showGenerateWorkleaseModal,
    setShowGenerateWorkleaseModal,
  ] = useState<boolean>(false);

  const [
    getEvaluatedInputConfigResults,
    { loading: isEvaluatingInputConfigLoading },
  ] = useGetEvaluatedReportInputConfigResultByCreditAgreementIdLazyQueryGQLType(
    {
      fetchPolicy: 'network-only',
      onCompleted: ({ inputConfig }) => {
        if (inputConfig.errors.length > 0) {
          toast({
            title: 'Reporting Input Config Evaluate Errors',
            description: inputConfig.errors.join(', '),
            status: 'error',
            position: 'top',
            duration: 10000,
            isClosable: true,
          });
          return;
        }

        setQueryResults(inputConfig.variables);
        setShowVariableModal(true);
      },
      onError: (error) => {
        const errorMessage = getErrorMessageFromApolloError({ error });
        toast({
          ...errorToastAdminConfig,
          title: 'An error occurred while attempting to evaluate inputs.',
          description: errorMessage,
        });
      },
    },
  );

  const { data: userData } = useGetUserQuery({});
  if (!userData || !userData.user) {
    return <Skeleton />;
  }
  const userId = userData.user.id;
  const initiatingOrganizationId = userData.user.organizationId;

  const isMutable = !!wipConfig?.isMutateAllowed(inputConfigPropertyKey);

  return (
    <Box id="reporting-input-config-container">
      <Flex alignItems="center" mb={isMutable ? '3' : '6'}>
        <Text as="h2" fontWeight="bold" fontSize="xl" mr="2">
          Input Config
        </Text>
        {wipConfig?.isMutateAllowed(inputConfigPropertyKey) && (
          <>
            <SaveIconButton
              ariaLabel="save input config"
              label="Save Input Config"
              onClick={() => {
                const { values } = wipConfig.formProps;

                const inputConfig = getIn(values, inputConfigPropertyKey);
                const inputConfigErrors = reportingValidate.input.validateSyncReportingInputConfig(
                  { config: inputConfig },
                );
                if (inputConfigErrors.length > 0) {
                  toast({
                    title: 'Reporting Input Config Errors',
                    description: inputConfigErrors.join(', '),
                    status: 'error',
                    position: 'top',
                    duration: 10000,
                    isClosable: true,
                  });
                  return;
                }

                wipConfig.saveCreditAgreementFullArtifactWip({
                  variables: {
                    creditAgreementFullArtifactWip: {
                      id: values.id,
                      creditFacilityId: values.creditFacilityId,
                      creditAgreement: JSON.stringify(values),
                    },
                  },
                });
              }}
            />

            {isEvaluatingInputConfigLoading ? (
              <Spinner size="sm" ml="2" p="2" mr="2" />
            ) : (
              <UploadIconButton
                ariaLabel="trigger saved inputs"
                label="Trigger Saved Inputs"
                onClick={() => {
                  setShowGenerateWorkleaseModal(true);
                }}
              />
            )}

            <Tooltip
              label={
                queryResults.length > 0
                  ? 'View Evaluated Inputs'
                  : 'Need to Trigger Saved Inputs'
              }
            >
              <Box>
                <IconButton
                  variant="ghost"
                  aria-label="view evaluted inputs"
                  icon={<FiSearch />}
                  isDisabled={queryResults.length === 0}
                  onClick={() => {
                    setShowVariableModal(true);
                  }}
                />
              </Box>
            </Tooltip>
          </>
        )}
      </Flex>

      {isMutable && (
        <>
          <Text fontStyle="italic" fontSize="md" mb="2">
            Note that this &quot;save&quot; only saves the Input config portion
          </Text>
          <Text fontStyle="italic" fontSize="md" mb="6">
            As part of the Reporting worklease structure,
            &quot;reportingDate&quot; will be an available variable by default
            that can be used
          </Text>
        </>
      )}

      <ReportingGlobalRecurringDateConfigs
        wipConfig={wipConfig}
        globalRecurringDateConfigs={_config_.input.globalRecurringDateConfigs}
        basePropertyKey={`${inputConfigPropertyKey}.globalRecurringDateConfigs`}
        source="reporting"
      />

      <AthenaQueriesView
        organizationId={organizationId}
        wipConfig={wipConfig}
        athenaQueries={_config_.input.athenaQueries}
        basePropertyKey={`${inputConfigPropertyKey}.athenaQueries`}
        inputDateType="reportingDate"
        validationVariables={
          reportingValidate.util.SUPPLEMENTAL_QUERY_VARIABLES
        }
      />

      <AthenaRecurringQueriesContainer
        organizationId={organizationId}
        wipConfig={wipConfig}
        athenaRecurringQueries={_config_.input.athenaRecurringQueries}
        globalRecurringDateConfigs={_config_.input.globalRecurringDateConfigs}
        basePropertyKey={`${inputConfigPropertyKey}.athenaRecurringQueries`}
        inputDateType="reportingDate"
        validationVariables={
          reportingValidate.util.SUPPLEMENTAL_QUERY_VARIABLES
        }
      />

      <ReportingDataAccessors
        wipConfig={wipConfig}
        dataAccessors={_config_.input.dataAccessors}
        basePropertyKey={`${inputConfigPropertyKey}.dataAccessors`}
      />

      <ReportingIntermediateFields
        wipConfig={wipConfig}
        intermediateFields={_config_.input.intermediateFields}
        basePropertyKey={`${inputConfigPropertyKey}.intermediateFields`}
        configPropertyKey={inputConfigPropertyKey}
      />

      <ReportingGenerateWorkleaseModal
        title="Evaluate Input Config"
        isOpen={showGenerateWorkleaseModal}
        onCancel={() => {
          setShowGenerateWorkleaseModal(false);
        }}
        onExecute={({
          worklease,
        }: {
          worklease: Reporting.ReportingGenerateWorklease;
        }) => {
          getEvaluatedInputConfigResults({ variables: { worklease } });
        }}
        creditAgreementId={wipConfig?.formProps.values.id ?? null}
        creditFacilityId={wipConfig?.formProps.values.creditFacilityId ?? null}
        reportConfigTypes={reportConfigTypes}
        reportName={reportName}
        reportTemplateFileType={_config_.type}
        userId={userId}
        initiatingOrganizationId={initiatingOrganizationId}
        showParameterEditor={false}
      />

      <VariablePreviewModal
        variables={queryResults}
        isOpen={showVariableModal}
        onClose={() => setShowVariableModal(false)}
      />
    </Box>
  );
};
